import { Visibility } from '@numistic/contracts'
import { DataError, useCreateCatalogItem, useMeta, useSession } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Card, CardContent, CardHeader, CardTitle, Field, Select } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import { PageLoading } from '../../components/page-loading.js'
import { autoTitle, CATALOG_REF_KINDS } from '../../catalog/shared.js'
import { useErrorText } from '../../lib/errors.js'
import { requireSession } from '../../lib/guards.js'
import { MetaForm } from '../../meta/meta-form.js'
import { useRefResolver } from '../../meta/ref-resolver.js'
import { rootRoute } from '../root.js'

export const catalogNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/new',
  validateSearch: z.object({ type: z.string().optional() }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: NewCatalogItemPage,
})

function NewCatalogItemPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { type } = catalogNewRoute.useSearch()
  const meta = useMeta().data
  const { data: session } = useSession()
  const navigate = useNavigate()
  const create = useCreateCatalogItem()
  const resolve = useRefResolver(meta, CATALOG_REF_KINDS)
  const editor = session?.user.role === 'admin' || session?.user.role === 'editor'
  const [visibility, setVisibility] = useState<Visibility>(editor ? 'public' : 'private')
  const prepare = useMemo(
    () => (meta && type ? autoTitle(meta, type, resolve) : undefined),
    [meta, type, resolve],
  )

  if (!meta) return <PageLoading />
  const itemType = meta.itemTypes.find((x) => x.code === type && x.active !== false)

  if (!itemType)
    return (
      <div className="grid gap-4">
        <h1 className="text-2xl font-semibold">{t('catalog.chooseType')}</h1>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {meta.itemTypes
            .filter((x) => x.active !== false)
            .map((x) => (
              <li key={x.code}>
                <Link
                  to="/catalog/new"
                  search={{ type: x.code }}
                  className="block rounded-lg border bg-card p-4 text-center font-medium hover:border-primary"
                >
                  {x.name.ru}
                </Link>
              </li>
            ))}
        </ul>
      </div>
    )

  const issues = create.error instanceof DataError ? create.error.issues : undefined
  return (
    <Card className="max-w-4xl">
      <CardHeader>
        <CardTitle>
          {t('catalog.newTitle')}: {itemType.name.ru}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <MetaForm
          typeCode={itemType.code}
          level="catalog"
          prepare={prepare}
          serverIssues={issues}
          submitLabel={t('catalog.create')}
          header={
            editor ? (
              <Field label={t('catalog.visibility')} className="max-w-xs">
                <Select
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as Visibility)}
                >
                  {Visibility.options.map((v) => (
                    <option key={v} value={v}>
                      {t(`catalog.visibilities.${v}`)}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null
          }
          footer={
            <>
              <p className="text-xs text-muted-foreground">{t('catalog.autoTitle')}</p>
              {create.error ? (
                <Alert variant="destructive">{errorText.request(create.error)}</Alert>
              ) : null}
            </>
          }
          pending={create.isPending}
          onSubmit={(values) =>
            create.mutate(
              { typeCode: itemType.code, visibility, values },
              {
                onSuccess: (item) =>
                  void navigate({
                    to: '/catalog/$id/edit',
                    params: { id: item.id },
                    search: { tab: 'images' },
                  }),
              },
            )
          }
        />
      </CardContent>
    </Card>
  )
}
